import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal, Select, Textarea } from "@/components/ui";
import { api } from "@/lib/api";
import type { Group } from "@/lib/types";
import { useGroups } from "./Users";

interface Endpoint {
  id: string;
  name: string;
  base_url: string;
  has_api_key: boolean;
  enabled: boolean;
  new_models_enabled: boolean;
  extra_body: Record<string, unknown>;
}

interface AdminModel {
  id: string;
  endpoint_id: string;
  model_id: string;
  display_name: string | null;
  name: string;
  enabled: boolean;
  available: boolean;
  caps: Record<string, boolean | number>;
  params: Record<string, unknown>;
  groups: Group[];
}

const CAPS = [
  { key: "vision", label: "Vision (images)" },
  { key: "tools", label: "Tool calling" },
  { key: "audio", label: "Audio input" },
];

export default function Endpoints() {
  const qc = useQueryClient();
  const endpoints = useQuery({ queryKey: ["admin", "endpoints"], queryFn: () => api.get<Endpoint[]>("/api/admin/endpoints") });
  const models = useQuery({ queryKey: ["admin", "models"], queryFn: () => api.get<AdminModel[]>("/api/admin/models") });
  const [editing, setEditing] = useState<Endpoint | "new" | null>(null);
  const [editingModel, setEditingModel] = useState<AdminModel | null>(null);
  const [filter, setFilter] = useState("");
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin"] });
    qc.invalidateQueries({ queryKey: ["models"] });
  };
  const refresh = useMutation({
    mutationFn: (id: string) => api.post<{ added: string[]; missing: string[]; total: number }>(`/api/admin/endpoints/${id}/refresh`),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/admin/endpoints/${id}`), onSuccess: invalidate });
  const toggle = useMutation({
    mutationFn: (m: AdminModel) => api.patch(`/api/admin/models/${m.id}`, { enabled: !m.enabled }),
    onSuccess: invalidate,
  });

  return (
    <>
      <SiteDefault models={(models.data ?? []).filter((m) => m.enabled)} endpoints={endpoints.data ?? []} />
      <Card
        title="Endpoints"
        actions={
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus size={16} /> Add endpoint
          </Button>
        }
      >
        <p className="mb-3 text-sm text-zinc-500">OpenAI-compatible servers (llama-swap, vLLM, Ollama, OpenAI…). Models are listed from their /models endpoint.</p>
        <ErrorText error={refresh.error || remove.error} />
        {refresh.data && (
          <p className="mb-2 text-sm text-zinc-500">
            Found {refresh.data.total} models{refresh.data.added.length ? `, ${refresh.data.added.length} new` : ""}
            {refresh.data.missing.length ? `, ${refresh.data.missing.length} no longer listed` : ""}.
          </p>
        )}
        {endpoints.data?.length === 0 && <p className="text-sm text-zinc-500">No endpoints yet.</p>}
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {endpoints.data?.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium">
                  {e.name} {!e.enabled && <Badge tone="red">disabled</Badge>} {!e.new_models_enabled && <Badge tone="amber">whitelist</Badge>}
                </p>
                <p className="truncate font-mono text-xs text-zinc-500">{e.base_url}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="icon" onClick={() => refresh.mutate(e.id)} disabled={refresh.isPending} aria-label="Refresh models" title="Refresh models">
                  <RefreshCw size={16} className={refresh.isPending && refresh.variables === e.id ? "animate-spin" : ""} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setEditing(e)} aria-label="Edit">
                  <Pencil size={16} />
                </Button>
                <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => confirm(`Delete endpoint ${e.name} and its model settings?`) && remove.mutate(e.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Models" actions={<Input className="h-8 max-w-48" placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} />}>
        <p className="mb-3 text-sm text-zinc-500">Enabled models with no groups are available to everyone; with groups, only to their members. Admins see all enabled models.</p>
        {endpoints.data?.map((e) => {
          const list = (models.data ?? []).filter((m) => m.endpoint_id === e.id && m.model_id.toLowerCase().includes(filter.toLowerCase()));
          if (!list.length) return null;
          return (
            <div key={e.id} className="mb-4">
              <p className="mb-1 text-xs font-medium tracking-wide text-zinc-500 uppercase">{e.name}</p>
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {list.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 py-2">
                    <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={m.enabled} onChange={() => toggle.mutate(m)} aria-label={`Enable ${m.model_id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm">
                        <span className="truncate">{m.name}</span>
                        {m.display_name && <span className="font-mono text-xs text-zinc-500">{m.model_id}</span>}
                        {!m.available && <Badge tone="amber">not listed</Badge>}
                        {m.caps.tools === false && <Badge tone="amber">no tools</Badge>}
                        {CAPS.filter((c) => c.key !== "tools" && m.caps[c.key]).map((c) => (
                          <Badge key={c.key} tone="indigo">
                            {c.key}
                          </Badge>
                        ))}
                        {m.groups.map((g) => (
                          <Badge key={g.id}>{g.name}</Badge>
                        ))}
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setEditingModel(m)} aria-label="Model settings">
                      <Pencil size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </Card>
      {editing && <EndpointForm endpoint={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={invalidate} />}
      {editingModel && <ModelForm model={editingModel} onClose={() => setEditingModel(null)} onSaved={invalidate} />}
    </>
  );
}

function SiteDefault({ models, endpoints }: { models: AdminModel[]; endpoints: Endpoint[] }) {
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ["admin", "defaults"], queryFn: () => api.get<{ model_id: string | null }>("/api/admin/defaults") });
  const save = useMutation({
    mutationFn: (id: string) => api.put("/api/admin/defaults", { model_id: id || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "defaults"] });
      qc.invalidateQueries({ queryKey: ["models"] });
    },
  });
  return (
    <Card title="Default model">
      <Label label="Model for new chats" hint="For everyone who has not picked their own default (Settings) and has not used a model yet in their browser.">
        <Select className="max-w-sm" value={current.data?.model_id ?? ""} onChange={(e) => save.mutate(e.target.value)}>
          <option value="">First available model</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({endpoints.find((e) => e.id === m.endpoint_id)?.name})
            </option>
          ))}
        </Select>
      </Label>
      <ErrorText error={save.error} />
    </Card>
  );
}

function JsonField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (v: string) => void }) {
  let error = "";
  try {
    if (value.trim()) JSON.parse(value);
  } catch {
    error = "Not valid JSON";
  }
  return (
    <Label label={label} hint={error || hint}>
      <Textarea className="font-mono text-xs" rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
    </Label>
  );
}

function parseJson(text: string): Record<string, unknown> {
  return text.trim() ? JSON.parse(text) : {};
}

function EndpointForm({ endpoint, onClose, onSaved }: { endpoint: Endpoint | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: endpoint?.name ?? "",
    base_url: endpoint?.base_url ?? "http://",
    api_key: "",
    enabled: endpoint?.enabled ?? true,
    new_models_enabled: endpoint?.new_models_enabled ?? true,
    extra_body: endpoint && Object.keys(endpoint.extra_body).length ? JSON.stringify(endpoint.extra_body, null, 2) : "",
  });
  const [clearKey, setClearKey] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        api_key: clearKey ? "" : form.api_key || (endpoint ? null : ""),
        extra_body: parseJson(form.extra_body),
      };
      return endpoint ? api.patch(`/api/admin/endpoints/${endpoint.id}`, body) : api.post("/api/admin/endpoints", body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <Modal open onClose={onClose} title={endpoint ? "Edit endpoint" : "Add endpoint"} wide>
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Label>
          <Label label="Base URL" hint="Including /v1, e.g. http://bigthink:8080/v1">
            <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} required />
          </Label>
        </div>
        <Label label="API key" hint={endpoint?.has_api_key ? "A key is stored. Leave empty to keep it." : "Optional."}>
          <Input type="password" autoComplete="off" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} disabled={clearKey} />
        </Label>
        {endpoint?.has_api_key && <Checkbox label="Remove the stored key" checked={clearKey} onChange={(e) => setClearKey(e.target.checked)} />}
        <JsonField
          label="Extra request body (JSON)"
          hint='Merged into every chat request, e.g. {"chat_template_kwargs": {"enable_thinking": false}}'
          value={form.extra_body}
          onChange={(v) => setForm({ ...form, extra_body: v })}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox label="Enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <Checkbox
            label="Enable newly found models automatically"
            checked={form.new_models_enabled}
            onChange={(e) => setForm({ ...form, new_models_enabled: e.target.checked })}
          />
        </div>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function ContextLengthField({ model, value, onChange }: { model: AdminModel; value: boolean | number | undefined; onChange: (v: number | null) => void }) {
  const detect = useMutation({
    mutationFn: () => api.post<AdminModel>(`/api/admin/models/${model.id}/detect-context`),
    onSuccess: (m) => onChange(Number(m.caps.context_length) || null),
  });
  return (
    <div className="space-y-1.5">
      <Label label="Context length (tokens)">
        <div className="flex gap-2">
          <Input
            type="number"
            min={1}
            value={typeof value === "number" ? value : ""}
            placeholder="unknown"
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          />
          <Button type="button" variant="secondary" disabled={detect.isPending} onClick={() => detect.mutate()}>
            Ask server
          </Button>
        </div>
      </Label>
      <p className="text-xs text-zinc-500">
        Used for the fill gauge in chats and to refuse conversations that cannot fit. Refreshing the endpoint fills it in when the server reports it
        (vLLM, LiteLLM); "Ask server" also tries llama.cpp /props (llama-swap loads the model to answer).
      </p>
      <ErrorText error={detect.error} />
    </div>
  );
}

function ModelForm({ model, onClose, onSaved }: { model: AdminModel; onClose: () => void; onSaved: () => void }) {
  const groups = useGroups();
  const [form, setForm] = useState({
    display_name: model.display_name ?? "",
    enabled: model.enabled,
    caps: { ...model.caps },
    params: Object.keys(model.params).length ? JSON.stringify(model.params, null, 2) : "",
    group_ids: model.groups.map((g) => g.id),
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/api/admin/models/${model.id}`, { ...form, params: parseJson(form.params) }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Modal open onClose={onClose} title={model.model_id} wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Label label="Display name">
          <Input value={form.display_name} placeholder={model.model_id} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </Label>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Capabilities</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {CAPS.map((c) => (
              <Checkbox
                key={c.key}
                label={c.label}
                checked={c.key === "tools" ? form.caps.tools !== false : !!form.caps[c.key]}
                onChange={(e) => setForm({ ...form, caps: { ...form.caps, [c.key]: e.target.checked } })}
              />
            ))}
          </div>
        </div>
        <ContextLengthField
          model={model}
          value={form.caps.context_length}
          onChange={(v) => {
            const { context_length: _drop, ...rest } = form.caps;
            setForm({ ...form, caps: v ? { ...rest, context_length: v } : rest });
          }}
        />
        <JsonField label="Default parameters (JSON)" hint='e.g. {"temperature": 0.7, "max_tokens": 4096}' value={form.params} onChange={(v) => setForm({ ...form, params: v })} />
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Restrict to groups</span>
          {groups.data?.length ? (
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {groups.data.map((g) => (
                <Checkbox
                  key={g.id}
                  label={g.name}
                  checked={form.group_ids.includes(g.id)}
                  onChange={() =>
                    setForm({ ...form, group_ids: form.group_ids.includes(g.id) ? form.group_ids.filter((x) => x !== g.id) : [...form.group_ids, g.id] })
                  }
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No groups defined; the model is available to everyone.</p>
          )}
        </div>
        <Checkbox label="Enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
